// utils/fetchWithRetry.ts
// Wraps fetch with a request timeout (AbortController) and retry/backoff for
// transient failures (network errors, timeouts, 5xx). Never retries 4xx —
// those are not going to succeed on a second try.

type RetryOptions = {
    retries?: number;
    timeoutMs?: number;
    backoffMs?: number;
};

function isRetriableStatus(status: number): boolean {
    return status >= 500 || status === 429;
}

function sleep(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
}

export async function fetchWithRetry(
    input: string,
    init: RequestInit = {},
    { retries = 2, timeoutMs = 8000, backoffMs = 500 }: RetryOptions = {}
): Promise<Response> {
    let lastError: unknown;

    for (let attempt = 0; attempt <= retries; attempt++) {
        const controller = new AbortController();
        const timer = setTimeout(() => controller.abort(), timeoutMs);

        try {
            const response = await fetch(input, {
                ...init,
                signal: controller.signal,
            });

            clearTimeout(timer);

            if (!response.ok && isRetriableStatus(response.status) && attempt < retries) {
                lastError = new Error(`HTTP error! status: ${response.status}`);
                await sleep(backoffMs * Math.pow(2, attempt));
                continue;
            }

            return response;
        } catch (error) {
            clearTimeout(timer);
            lastError = error;

            if (attempt < retries) {
                await sleep(backoffMs * Math.pow(2, attempt));
                continue;
            }
        }
    }

    throw lastError instanceof Error ? lastError : new Error('Request failed');
}
